'use client';

import 'rc-slider/assets/index.css';

import {
  useParams,
  useSearchParams,
} from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import ListingFilterNew from '@/features/listing/components/listings-filters/listing-filter-new/listing-filter-new';
import ListingFilterRange
  from '@/features/listing/components/listings-filters/listing-filter-range/listing-filter-range';
import ListingFilterSection
  from '@/features/listing/components/listings-filters/listing-filter-section/listing-filter-section';
import ListingFilterToggle
  from '@/features/listing/components/listings-filters/listing-filter-toggle/listing-filter-toggle';
import ListingRadiusFilter
  from '@/features/listing/components/listings-filters/listing-radius-filter/listing-radius-filter';
import { LISTING_URL_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import {
  APARTMENT_AMENITIES_FILTER,
  BATHROOMS_FILTER,
  BEDROOMS_FILTER,
  HOUSEHOLD_SEX_FILTER,
  LISTINGS_AGE_FILTER,
  PET_OWN_FILTER,
  PET_PREFERRED_FILTER,
  SEX_FILTER,
  ZODIAC_FILTER,
} from '@/features/listing/constants/listings-filters-constants';
import {
  FiltersNames,
  type ListingsFilterName,
  type ListingsRangeFilter,
  PaginationNames,
} from '@/features/listing/types/listing-filters-types';
import { type ListingUrl } from '@/features/listing/types/listing-types';
import { useRouter } from '@/i18n/routing';

interface ListingDetailedFiltersProps {
  onFilterDisplayChange: (filterName: ListingsFilterName | null) => void;
  setFilterLink: (link: { pathname: string; searchParams: URLSearchParams }) => void;
  filterLink: {
    pathname: string;
    searchParams: URLSearchParams;
  }
}

export default function ListingDetailedFilters(props: ListingDetailedFiltersProps) {
  const {
    filterLink,
    onFilterDisplayChange,
    setFilterLink,
  } = props;

  const params = useParams();
  const currentSearchParams = useSearchParams();
  const router = useRouter();

  const [isPopoverOpen,
    setPopoverOpen] = useState(false);

  const translationsFilters = useTranslations('listing.filters');

  const filters = {
    haveapartment: [BEDROOMS_FILTER, BATHROOMS_FILTER, APARTMENT_AMENITIES_FILTER],
    haveshare: [LISTINGS_AGE_FILTER, HOUSEHOLD_SEX_FILTER, ZODIAC_FILTER, PET_PREFERRED_FILTER],
    needapartment: [BEDROOMS_FILTER, APARTMENT_AMENITIES_FILTER],
    needroom: [LISTINGS_AGE_FILTER, SEX_FILTER, ZODIAC_FILTER, PET_OWN_FILTER],
  };

  const paramsListingType = params.listingType ?? 'rooms-for-rent';

  const currentListingType = LISTING_URL_PART_VARIANTS[paramsListingType as ListingUrl];
  const currentFilters = filters[currentListingType];

  let activeFiltersCount = 0;
  // TODO: FIX ERROR TypeError: Cannot read properties of undefined (reading 'forEach')
  currentFilters.forEach((filter) => {
    if (filter.filterType === 'range') {
      if (currentSearchParams.has(filter.options.max.name) || currentSearchParams.has(filter.options.min.name)) {
        activeFiltersCount += 1;
      }
    } else if (currentSearchParams.has(filter.name)) {
      activeFiltersCount += 1;
    }
  });

  if (currentSearchParams.has(FiltersNames.GeoRadiusScale)) {
    activeFiltersCount += 1;
  }

  const handleFiltersClear = () => {
    const {
      pathname,
      searchParams,
    } = filterLink;

    currentFilters.forEach((filter) => {
      if (filter.filterType === 'range') {
        searchParams.delete(filter.options.min.name);
        searchParams.delete(filter.options.max.name);
      } else {
        searchParams.delete(filter.name);
      }
    });

    searchParams.delete('search_params.geo.radius_scale');

    setFilterLink({
      pathname,
      searchParams,
    });
  };

  const handleChangeFilterLink = (updatedFilterLink: ListingDetailedFiltersProps['filterLink']) => {
    if (updatedFilterLink.searchParams.has(PaginationNames.PageNumber)) {
      updatedFilterLink.searchParams.delete(PaginationNames.PageNumber);
    }

    setFilterLink(updatedFilterLink);
  };

  const handleApplyClick = () => {
    router.push(`${filterLink.pathname}?${filterLink.searchParams.toString()}`);
  };

  return (
    <ListingFilterNew
      activeFiltersCount={activeFiltersCount}
      buttonText={translationsFilters('common.button')}
      dialogTitle={translationsFilters('common.dropdown-title')}
      onFilterDisplayChange={onFilterDisplayChange}
      listingFilterName="listingsDetailed"
      isPopoverOpen={isPopoverOpen}
      setPopoverOpen={setPopoverOpen}
      onClear={handleFiltersClear}
      onApplyClick={handleApplyClick}
    >
      <ListingFilterSection title={translationsFilters('filter-titles.radius')}>
        <ListingRadiusFilter filterLink={filterLink} setFilterLink={handleChangeFilterLink} />
      </ListingFilterSection>

      {currentFilters.map((filter) => {
        if (filter.filterType === 'checkbox' || filter.filterType === 'radio') {
          return (
            // @ts-expect-error - add all possible values
            <ListingFilterSection key={filter.filterTitle} title={translationsFilters(`filter-titles.${filter.filterTitle}`)}>
              <ListingFilterToggle
                filterLink={filterLink}
                filter={filter}
                columns={filter.columns}
                setFilterLink={handleChangeFilterLink}
              />
            </ListingFilterSection>
          );
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (filter.filterType === 'range') {
          const cloneFilter = JSON.stringify(filter);
          const updatedFilter = JSON.parse(cloneFilter) as ListingsRangeFilter;
          updatedFilter.options.min.value = Number(filterLink.searchParams.get(filter.options.min.name)) || filter.options.min.defaultValue;
          updatedFilter.options.max.value = Number(filterLink.searchParams.get(filter.options.max.name)) || filter.options.max.defaultValue;

          return (
            // @ts-expect-error - add all possible values
            <ListingFilterSection key={filter.filterTitle} title={translationsFilters(`filter-titles.${filter.filterTitle}`)}>
              <ListingFilterRange filterLink={filterLink} filter={updatedFilter} setFilterLink={handleChangeFilterLink} />
            </ListingFilterSection>
          );
        }

        return null;
      })}
    </ListingFilterNew>
  );
}
